import React from 'react';
import { Users, MapPin, ShoppingCart, Truck, Package, Target, BarChart3, CheckCircle2 } from 'lucide-react';
import { distributionModel, images } from '../data/mock';
import { PageSEO } from '../components/PageSEO';

const DistributionModel = () => {
  const advantages = [
    {
      icon: Target,
      title: "Cobertura Territorial",
      description: "Presencia en todos los segmentos del mercado: tiendas, supermercados y canales especializados"
    },
    {
      icon: BarChart3,
      title: "Seguimiento y Control",
      description: "Monitoreo constante de ventas, inventarios y ejecución en punto de venta"
    },
    {
      icon: CheckCircle2,
      title: "Gestión Profesional",
      description: "Equipo capacitado en técnicas de venta, merchandising y atención al cliente"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <PageSEO path="/modelo" />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              Modelo de Distribución
            </h1>
            <p className="text-xl sm:text-2xl text-emerald-100 leading-relaxed">
              Estrategia integral para garantizar el éxito de nuestras marcas aliadas
            </p>
          </div>
        </div>
      </section>

      {/* Main Distribution Model */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4">
              Nuestro Modelo
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Cuatro Pilares Fundamentales
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Una estrategia probada que garantiza resultados efectivos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {distributionModel.map((item, index) => {
              const iconMap = {
                users: Users,
                'map-pin': MapPin,
                'shopping-cart': ShoppingCart,
                truck: Truck
              };
              const Icon = iconMap[item.icon];

              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl bg-gradient-to-br from-gray-50 to-emerald-50 hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-emerald-500"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon size={32} className="text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section with Image */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={images.warehouse}
                alt="Centro de distribución moderno"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">
                Nuestra Operación
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Logística Desde Base Central
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Package className="text-emerald-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Gestión de Inventarios
                    </h4>
                    <p className="text-gray-600">
                      Control exhaustivo de stock para garantizar disponibilidad permanente de productos
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Truck className="text-teal-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Rutas Optimizadas
                    </h4>
                    <p className="text-gray-600">
                      Planificación estratégica de rutas para maximizar eficiencia y cobertura
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Target className="text-emerald-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Entrega Puntual
                    </h4>
                    <p className="text-gray-600">
                      Cumplimiento de tiempos de entrega para mantener la satisfacción del cliente
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ventajas Competitivas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Lo que nos diferencia en el mercado de distribución
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div
                  key={index}
                  className="p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 hover:shadow-lg transition-shadow"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full mb-6">
                    <Icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet Image Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={images.fleet}
              alt="Flota de distribución"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent flex items-end">
              <div className="p-8 text-white">
                <h3 className="text-3xl font-bold mb-2">Flota Moderna</h3>
                <p className="text-xl text-gray-300">
                  Equipamiento de última generación para garantizar la calidad del servicio
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            ¿Listo para Optimizar tu Distribución?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
            Nuestro modelo probado puede ayudarte a alcanzar tus objetivos comerciales
          </p>
          <a
            href="/contacto"
            className="inline-block px-8 py-4 bg-white text-emerald-700 font-semibold rounded-lg hover:bg-emerald-50 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Solicita una Reunión
          </a>
        </div>
      </section>
    </div>
  );
};

export default DistributionModel;
